import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getCurrentWeekNumber } from "../../utils/leagueUtils";
import { FIRST_PLAYOFF_WEEK } from "../../utils/season";

// Lineup weeks only. CFB week 0 is the late-August opening weekend — it plays
// but scores nothing, so there is no lineup or standings row to select for it.
// See SEASON_WEEKS in utils/season.js for the full calendar.
//
// Left/right arrows, not a scrollable row of every week's own button: with up
// to 13 weeks in a season, most of that row was always disabled placeholders
// for weeks that haven't happened yet, and it read as clutter rather than a
// control. This matches the week switcher already used on the Scoreboard tab.
const WeekSelector = ({
  currentWeek,
  viewMode,
  setViewMode,
  selectedWeek,
  setSelectedWeek,
  availableWeeks,
  standingsLoading,
  loadWeeklyStandings,
  isLive,
}) => {
  const currentWeekNum = getCurrentWeekNumber(currentWeek);

  // Which weeks have a stored snapshot to show (historical weeks + the
  // current, live one).
  const weeksWithData = new Set([...availableWeeks, currentWeekNum]);
  const displayedWeek = viewMode === "current" ? currentWeekNum : (selectedWeek ?? currentWeekNum);
  const isPlayoffWeek = displayedWeek >= FIRST_PLAYOFF_WEEK;

  const goToWeek = (week) => {
    if (week === currentWeekNum) {
      setViewMode("current");
      return;
    }
    if (!weeksWithData.has(week)) return;
    setViewMode("historical");
    setSelectedWeek(week);
    loadWeeklyStandings(week);
  };

  // Left stops at week 1, or one step early if a gap in the snapshot history
  // means the week behind it has nothing to show. Right never goes past the
  // current week: there is nothing to show for a week that hasn't happened.
  const canGoBack = displayedWeek > 1 && weeksWithData.has(displayedWeek - 1);
  const canGoForward = displayedWeek < currentWeekNum;

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 min-w-0">
        <button
          onClick={() => goToWeek(displayedWeek - 1)}
          disabled={!canGoBack}
          aria-label="Previous week"
          style={{ background: "none", border: "none", padding: 4, color: canGoBack ? "var(--color-brand)" : "var(--color-text-faint)" }}
        >
          <ChevronLeft size={18} />
        </button>
        <span className="text-sm font-bold flex-shrink-0" style={{ color: "var(--color-text-strong)" }}>
          Week {displayedWeek}
        </span>
        {isPlayoffWeek && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{ backgroundColor: "var(--color-captain-fill)", color: "var(--color-captain-text)" }}>
            Playoffs
          </span>
        )}
        {/* Only when the current week actually has a game live, not merely
            because it is the current week. */}
        {displayedWeek === currentWeekNum && isLive && (
          <span className="w-2 h-2 rounded-full animate-pulse flex-shrink-0" style={{ backgroundColor: "var(--color-success)" }} />
        )}
        <button
          onClick={() => goToWeek(displayedWeek + 1)}
          disabled={!canGoForward}
          aria-label="Next week"
          style={{ background: "none", border: "none", padding: 4, color: canGoForward ? "var(--color-brand)" : "var(--color-text-faint)" }}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {standingsLoading && (
        <span className="text-[11px] flex-shrink-0" style={{ color: "var(--color-text-muted)" }}>Loading...</span>
      )}
    </div>
  );
};

export default WeekSelector;
